import React, { useState, useEffect } from 'react'
import { redirect, useNavigate } from 'react-router-dom';
import { useParams } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { downloadAllSelectedIDsRecords } from './../../../../services/global_redux/globale_actions/Download.action';
import $ from 'jquery';
import { getReportRegistryEntry, loadRegistryReport, isMonthLockedReport, filterRowsByBranch, isValidPayrollDate, toPayrollMonthKey } from '../reportRegistry';
import { getInputMatrixActivities } from '../../../../services/register_redux/register_actions/Form.action';
import { getCurrentMonth } from '../../../../services/global_redux/globale_actions/Currentmonth.action';
import { fetchgenerateAttendanceRegister } from '../../../../services/register_redux/register_actions/AttendanceRegister.action';
import { getLeaveMaster } from '../../../../services/register_redux/register_actions/Leave.action';
import ReportEditableTable from '../ReportEditableTable';

const ATTENDANCE_LEAVE_CODE_PRIORITY = ['SL', 'CL', 'EL', 'PL', 'ML'];

const sortAttendanceLeaveCodes = (leaveCodes) => {
    const priority = ATTENDANCE_LEAVE_CODE_PRIORITY;
    return [...leaveCodes].sort((a, b) => {
        const ia = priority.indexOf(a);
        const ib = priority.indexOf(b);
        const aKnown = ia !== -1;
        const bKnown = ib !== -1;
        if (aKnown && bKnown) return ia - ib;
        if (aKnown && !bKnown) return ia - priority.length + 1;
        if (!aKnown && bKnown) return priority.length - 1 - ib;
        return a.localeCompare(b);
    });
};

const buildAttendanceDayHeaders = (year, month) => {
    const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const lastDay = new Date(year, month, 0).getDate();
    const headers = [];
    for (let day = 1; day <= lastDay; day++) {
        const dayString = String(day).padStart(2, '0');
        headers.push({
            key: `day_${dayString}`,
            label: `${monthNames[month - 1]}-${dayString}`,
        });
    }
    return headers;
};

const flattenAttendanceRegisterRows = (rows, leaveCodes) =>
    rows.map((row) => {
        const lb = row.leave_balances || {};
        const flat = { ...row };
        leaveCodes.forEach((code) => {
            const vals = lb[code] || {};
            flat[`lb_${code}_opening_balance`] = vals.opening_balance ?? '';
            flat[`lb_${code}_eligible_for_month`] = vals.eligible_for_month ?? '';
            flat[`lb_${code}_total_available`] = vals.total_available ?? '';
            flat[`lb_${code}_taken`] = vals.taken ?? '';
            flat[`lb_${code}_balance`] = vals.balance ?? '';
        });
        return flat;
    });

const buildAttendanceRegisterHeaders = (leaveCodes, year, month, leaveMasterRows) => {
    const fixed = [
        { key: 'emp_code', label: 'Employee Code' },
        { key: 'emp_Name', label: 'Employee Name' },
        { key: 'client_id', label: 'Organization ID' },
        { key: 'unit_id', label: 'Branch ID' },
    ];
    const codeToName = {};
    (leaveMasterRows || []).forEach((l) => {
        if (l.leave_code) {
            codeToName[l.leave_code] = l.leave_name || l.display_leave_code || l.leave_code;
        }
    });
    const leaveHeaders = [];
    sortAttendanceLeaveCodes(leaveCodes).forEach((code) => {
        const name = codeToName[code] || code;
        leaveHeaders.push(
            { key: `lb_${code}_opening_balance`, label: `${name} Opening Balance` },
            { key: `lb_${code}_eligible_for_month`, label: `${name} Eligible For The Month` },
            { key: `lb_${code}_total_available`, label: `${name} Total Available` },
            { key: `lb_${code}_taken`, label: `${name} Taken` },
            { key: `lb_${code}_balance`, label: `${name} Balance` },
        );
    });
    leaveHeaders.push({ key: 'total_leave_days', label: 'Total Leave Balance' });
    return [...fixed, ...leaveHeaders, ...buildAttendanceDayHeaders(year, month)];
};

const buildFallbackAttendanceColumns = (year, month) => [
    { key: 'emp_code', label: 'Employee Code' },
    { key: 'emp_Name', label: 'Employee Name' },
    { key: 'client_id', label: 'Organization ID' },
    { key: 'unit_id', label: 'Branch ID' },
    ...buildAttendanceDayHeaders(year, month),
];

async function loadAttendanceRegisterReport(dispatch, date) {
    const [year, month] = date.split('-').map((part) => parseInt(part, 10));
    const response = await dispatch(fetchgenerateAttendanceRegister(date));
    const generatedData = response?.data?.generated_attendance_register;
    const leave_codes = response?.data?.leave_codes || [];
    const leaveMasterResp = await dispatch(getLeaveMaster());
    const leaveMasterRows =
        leaveMasterResp?.success && Array.isArray(leaveMasterResp.data)
            ? leaveMasterResp.data
            : [];

    const columns =
        leave_codes.length > 0
            ? buildAttendanceRegisterHeaders(leave_codes, year, month, leaveMasterRows)
            : buildFallbackAttendanceColumns(year, month);

    if (response?.success && Array.isArray(generatedData) && generatedData.length > 0) {
        return {
            success: true,
            rows: flattenAttendanceRegisterRows(generatedData, leave_codes),
            columns,
        };
    }

    return {
        success: Boolean(response?.success),
        message: response?.message || 'Failed to load attendance register report',
        rows: [],
        columns,
    };
}

const PAYROLL_MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Cache payroll month list across remounts so month changes stay fast */
let payrollMonthsCache = null;

function usePayrollMonthFilter({ reportTitle, date, enabled }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [yearFilterList, setYearFilterList] = useState(payrollMonthsCache?.years || []);
    const [monthFilterList, setMonthFilterList] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [allMonthsData, setAllMonthsData] = useState(payrollMonthsCache?.months || []);

    useEffect(() => {
        if (!enabled) return;
        if (payrollMonthsCache) {
            setAllMonthsData(payrollMonthsCache.months);
            setYearFilterList(payrollMonthsCache.years);
            return;
        }
        const fetchPayrollMonths = async () => {
            try {
                const response = await dispatch(getInputMatrixActivities());
                if (response?.success === true) {
                    const months = response.data.months;
                    const years = [...new Set(months.map((item) => item.split('-')[0]))].sort(
                        (a, b) => Number(b) - Number(a)
                    );
                    payrollMonthsCache = { months, years };
                    setAllMonthsData(months);
                    setYearFilterList(years);
                }
            } catch (err) {
                console.error('Failed to fetch payroll months:', err);
            }
        };
        fetchPayrollMonths();
    }, [dispatch, enabled]);

    useEffect(() => {
        if (!enabled) return;
        if (!date || String(date).toLowerCase() === 'all') {
            setSelectedYear('');
            setSelectedMonth('');
            return;
        }
        const dateObject = new Date(date);
        if (Number.isNaN(dateObject.getTime())) return;
        const year = String(dateObject.getFullYear());
        const monthNum = String(dateObject.getMonth() + 1).padStart(2, '0');
        setSelectedYear(year);
        setSelectedMonth(PAYROLL_MONTH_NAMES[parseInt(monthNum, 10) - 1]);
    }, [date, enabled]);

    useEffect(() => {
        if (!enabled) return;
        if (!selectedYear) {
            setMonthFilterList([]);
            return;
        }
        const monthsForSelectedYear = [
            ...new Set(
                allMonthsData
                    .filter((item) => item.split('-')[0] === String(selectedYear))
                    .map((item) => {
                        const monthNum = parseInt(item.split('-')[1], 10);
                        return PAYROLL_MONTH_NAMES[monthNum - 1];
                    })
            ),
        ].sort((a, b) => PAYROLL_MONTH_NAMES.indexOf(a) - PAYROLL_MONTH_NAMES.indexOf(b));
        setMonthFilterList(monthsForSelectedYear);
    }, [selectedYear, allMonthsData, enabled]);

    const handleYearChange = (year) => {
        setSelectedYear(year);
        setSelectedMonth('');
        if (!year && !isMonthLockedReport(reportTitle)) {
            navigate(`/reports/report/${reportTitle}/all`, { replace: true });
        }
    };

    const handleMonthChange = (month) => {
        if (!month && !isMonthLockedReport(reportTitle)) {
            setSelectedMonth('');
            navigate(`/reports/report/${reportTitle}/all`, { replace: true });
            return;
        }
        if (!selectedYear || !month) return;
        const monthIdx = PAYROLL_MONTH_NAMES.indexOf(month);
        if (monthIdx < 0) return;
        setSelectedMonth(month);
        const monthNum = String(monthIdx + 1).padStart(2, '0');
        navigate(`/reports/report/${reportTitle}/${selectedYear}-${monthNum}-01`, { replace: true });
    };

    const periodFilters = enabled ? (
        <>
            <select
                className="editable-table-period-select"
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                aria-label="Payroll year"
            >
                <option value="">{isMonthLockedReport(reportTitle) ? "Year" : "All years"}</option>
                {yearFilterList.map((year) => (
                    <option key={year} value={year}>
                        {year}
                    </option>
                ))}
            </select>
            <select
                className="editable-table-period-select"
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                disabled={!selectedYear}
                aria-label="Payroll month"
            >
                <option value="">{isMonthLockedReport(reportTitle) ? "Month" : "All months"}</option>
                {monthFilterList.map((month) => (
                    <option key={month} value={month}>
                        {month}
                    </option>
                ))}
            </select>
        </>
    ) : null;

    return { periodFilters };
}

function ReportViews({ setLoading, showSnackbar }) {
    const { reportTitle ,date,date1 } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const dispatch = useDispatch();
    const [checkedItems, setCheckedItems] = useState({});
    const [checkedItemCount, setCheckedItemCount] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [isAllChecked, setIsAllChecked] = useState(false);
    // const [getDate1, setGetDate1] = useState(null);
    var getDate = date && String(date).toLowerCase() !== 'all' ? String(date).split('-') : [];
    // const getDate1 = date1.split('-');
    const client_ID = sessionStorage.getItem('selectedClient')
    const [filterType, setFilterType] = useState("monthly");
    const [toMonth, setToMonth] = useState("");
    const [fromYear, setFromYear] = useState("");
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [header,setHeader]=useState([]);
    const [availableYearMonths, setAvailableYearMonths] = useState(null);
    const [formattedTitle,setFormattedTitle]=useState(null);
    const registryConfig = getReportRegistryEntry(reportTitle);
    const usesRegistryReport = Boolean(registryConfig);
    const usesDedicatedReportApi = usesRegistryReport;

    const { periodFilters } = usePayrollMonthFilter({
        reportTitle,
        date,
        enabled: Boolean(registryConfig?.payrollMonthFilter),
    });

    const toTitleCase = (Title) => {
        return Title
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      };
    useEffect(() => {
        if (registryConfig) {
            setFormattedTitle(registryConfig.title);
            return;
        }
        setFormattedTitle(toTitleCase(reportTitle));
    }, [registryConfig, reportTitle]);

      const getReportsData = async () => {
        try {
            const client_ID = sessionStorage.getItem('selectedClient');
            const unit_ID = sessionStorage.getItem('selectedUnit');
            // Clear immediately so empty/previous rows don't linger while the new month loads
            setData([]);
            if (registryConfig?.columns?.length) {
                setHeader(registryConfig.columns);
            }

            if (isMonthLockedReport(reportTitle) && !isValidPayrollDate(date) && client_ID) {
                try {
                    const response = await dispatch(getCurrentMonth(client_ID));
                    const monthKey = toPayrollMonthKey(response?.data?.[0]?.current_month);
                    if (isValidPayrollDate(monthKey) && monthKey !== String(date || "")) {
                        navigate(`/reports/report/${reportTitle}/${monthKey}`, { replace: true });
                        return;
                    }
                } catch (err) {
                    console.error('Failed to resolve payroll month:', err);
                }
            }

            const payrollDate = isMonthLockedReport(reportTitle) ? toPayrollMonthKey(date) : date;

            if (registryConfig?.id === 'attendance-register') {
                if (!isValidPayrollDate(payrollDate)) {
                    showSnackbar?.('Select a payroll month to view attendance register.', 'warning');
                    setData([]);
                    setHeader([]);
                    return;
                }
                if (!client_ID) {
                    showSnackbar?.('Select organization to view report', 'warning');
                    setData([]);
                    setHeader([]);
                    return;
                }
                if (!unit_ID) {
                    showSnackbar?.('Select branch to view report', 'warning');
                    setData([]);
                    setHeader([]);
                    return;
                }
                setLoading?.(false);
                try {
                    const result = await loadAttendanceRegisterReport(dispatch, payrollDate);
                    if (result?.success) {
                        setData(filterRowsByBranch(
                            Array.isArray(result.rows) ? result.rows : [],
                            unit_ID,
                            'attendance-register'
                        ));
                        setHeader(Array.isArray(result.columns) ? result.columns : []);
                        setAvailableYearMonths(null);
                        setFilterType('monthly');
                    } else {
                        showSnackbar?.(result?.message || registryConfig.loadErrorMessage, 'error');
                        setData([]);
                        setHeader(Array.isArray(result.columns) ? result.columns : []);
                    }
                } catch (err) {
                    console.error('Failed to load attendance register report:', err);
                    showSnackbar?.(registryConfig.loadErrorMessage, 'error');
                    setData([]);
                    setHeader([]);
                } finally {
                    setLoading?.(false);
                }
                return;
            }

            if (registryConfig) {
                await loadRegistryReport({
                    config: registryConfig,
                    dispatch,
                    date: payrollDate,
                    clientId: client_ID,
                    unitId: unit_ID,
                    setLoading,
                    setData,
                    setHeader,
                    setAvailableYearMonths,
                    setFilterType,
                    showSnackbar,
                });
                return;
            }

            if (!client_ID) {
                showSnackbar?.('Select organization to view report', 'warning');
                setData([]);
                setHeader([]);
                return;
            }
            showSnackbar?.('This report is not available yet', 'warning');
            setData([]);
            setHeader([]);
            setAvailableYearMonths(null);
        } catch (err) {
            console.error("Failed to fetch reports:", err);
        }
    };

    useEffect(() => {
        getReportsData();
    }, [dispatch, date, date1, reportTitle]);

    


    // if(formattedTitle == "Employee Master"){
    //     header.push(
    //         { key: "emp_code", value: "Employee Code"},
    //         { key: "emp_name", value: "Employee Name" },
    //         { key: "unit_id", value: "Unit ID" },
    //         { key: "client_id", value: "Client ID" },
    //         { key: "fatherOrHusband", value: "Father Or Husband"},
    //         { key: "gender", value: "Gender"},
    //         { key: "dob", value: "Date Of Birth" },
    //         { key: "mail", value: "Mail Id"},
    //         { key: "postal_add", value: "Postal Address" },
    //         { key: "per_add", value: "Permanent Address" },
    //         { key: "designation", value: "Designation" },
    //         { key: "natural_of_work", value: "Nature Of Working" },
    //         { key: "department", value: "Deparment" },
    //         { key: "doj", value: "Date Of Joining"},
    //         { key: "esi_no", value: "ESI No" },
    //         { key: "pf_no", value: "PF No" },
    //         { key: "years_of_experiance", value: "Years Of Experiance"},
    //         { key: "comp_worked_with", value: "Companies Worked With" },
    //         { key: "bank_Acc_No", value: "Bank Account NO" },
    //         { key: "bank_ifsc", value: "Bank IFSC" },
    //         { key: "bank_name", value: "Bank Name" },
    //         { key: "bank_add", value: "Bank Address" },
    //         { key: "group_emp", value: "Group" },
    //         { key: "relay", value: "Relay" },
    //         { key: "shift_no", value: "Shift Number" }
    //     );
    // }else if(formattedTitle == "Salary Register"){
    //     header.push(
    //         { key: 'emp_code', value: 'Employee Code', input: 'string', readOnly:true   },
    //     { key: 'emp_name', value: 'Employee Name', input: 'string', readOnly:true   },
    //     { key: 'unit_id', value: 'Unit ID', input: 'string',readOnly:true  },
    //     { key: 'client_id', value: 'Client ID', input: 'string',readOnly:true  },
    //     { key: 'basic', value: 'Basic', input: 'number' },
    //     { key: 'hra', value: 'HRA', input: 'number' },
    //     { key: 'special_allowance', value: 'Speacial Allowance', input: 'number' },
    //     { key: 'da', value: 'DA', input: 'number' },
    //     { key: 'conveyance', value: 'Conveyance', input: 'number' },
    //     { key: 'ot', value: 'OT', input: 'number' },
    //     { key: 'nfh', value: 'NFH', input: 'number' },
    //     { key: 'maternityBenefit', value: 'Maternity Benefits', input: 'number' },
    //     { key: 'comp_others', value: 'Other Components', input: 'number' },
    //     { key: 'subsistanceAllowance', value: 'Subsistance Allowance', input: 'number' },
    //     { key: 'service_weightage', value: 'Service Weightage', input: 'string' },
    //     { key: 'st_bonus', value: 'Statutory Bonus', input: 'number' },
    //     { key: 'minimum_rates_of_wages_payable', value: 'Minimum Rates of Wages Payable', input: 'number' },
    //     { key: 'total_production_in_case_of_piece_rate', value: 'Total Production (Piece Rate)', input: 'number' },
    //     { key: 'ot_date', value: 'OT Date', input: 'string' },
    //     { key: 'total_overtime_hours_worked', value: 'Total Overtime Hours Worked', input: 'string' },
    //     { key: 'gross_salary', value: 'Gross Salary', input: 'number' },
    //     { key: 'esi', value: 'ESI', input: 'number' },
    //     { key: 'pf', value: 'PF', input: 'number' },
    //     { key: 'pt', value: 'PT', input: 'number' },
    //     { key: 'tds', value: 'TDS', input: 'number' },
    //     { key: 'lwf', value: 'LWF', input: 'number' },
    //     { key: 'society', value: 'Society', input: 'number' },
    //     { key: 'insurance', value: 'Insurance', input: 'number' },
    //     { key: 'damages_loss', value: 'Damages Loss', input: 'number' },
    //     { key: 'salary_advance', value: 'Salary Advance', input: 'number' },
    //     { key: 'fines', value: 'Fines', input: 'number' },
    //     { key: 'Act_of_Commission', value: 'Act of Commission', input: 'text' },
    //     { key: 'Cause_Against_Fine', value: 'Cause Against Fine', input: 'text' },
    //     { key: 'Fine_Imposed_Date', value: 'Fine Imposed Date', input: 'date' },
    //     { key: 'Fine_Realized_Date', value: 'Fine Realized Date', input: 'date' },
    //     { key: 'others', value: 'Others', input: 'number' },
    //     { key: 'total_deduction', value: 'Total Deduction', input: 'number' },
    //     { key: 'net_pay', value: 'Net Pay', input: 'number' },
    //     { key: 'mode_of_pay', value: 'Mode Of Pay', input: 'string' },
    //     )
    // }else if(formattedTitle == "Leave Register"){
    //     header.push(
    //         { key: "emp_code", value: "Employee Code", input: 'text', readOnly:true   },
    //     { key: "leave_code", value: "Leave Code", input: 'text', readOnly:true   },
    //     { key: "unit_id", value: "Unit ID", input: 'text',readOnly:true },
    //     { key: "client_id", value: "Client ID", input: 'text',readOnly:true  },
    //     { key: "leave_from", value: "Leave From", input: 'date' },
    //     { key: "leave_to", value: "Leave To", input: 'date' },
    //     { key: "total_days", value: "Total Days", input: 'number' },
    //     )

    // }else if(formattedTitle == "Contractor"){
    //     header.push(
    //         { key: "contractor_code", value: "Contractor Code", input: "text" },
    //         { key: "contractor_name", value: "Contractor Name", input: "text" },
    //         { key: "client_id", value: "Client ID", input: "text" , readOnly:true },
    //         { key: "unit_id", value: "Unit ID", input: "text" , readOnly:true },
    //         { key: "current_month", value: "Current Month", input: "date" },
    //         { key: "contractor_address", value: "Contractor Address", input: "text" },
    //         { key: "principal_employer_name", value: "Principal Employer Name", input: "text" },
    //         { key: "principal_employer_address", value: "Principal Employer Address", input: "text" },
    //         { key: "nature_of_work", value: "Nature of Work", input: "text" },
    //         { key: "work_location", value: "Work Location", input: "text" },
    //         { key: "contract_from_date", value: "Contract From Date", input: "date" },
    //         { key: "contract_to_date", value: "Contract TO Date", input: "date" },
    //         { key: "max_workmen", value: "Maximum Number of Workmen", input: "number" },
    //     )

    // }else if(formattedTitle == "Ctc Master"){
    //     header.push(
    //         { key: "emp_code", value: "Employee Code" , readOnly:true  },
    //     { key: "emp_name", value: "Employee Name" , readOnly:true  },
    //     { key: "client_id", value: "Client ID" ,readOnly:true },
    //     { key: "unit_id", value: "Unit ID" ,readOnly:true },
    //     { key: "basic", value: "Basic" },
    //     { key: "comp_others", value: "Comp Others" },
    //     { key: "conveyance", value: "Conveyance" },
    //     { key: "da", value: "DA" },
    //     { key: "damages_loss", value: "Damages Loss" },
    //     // { key: "emp_sig_or_thumb_imp", value: "Employee Signature Or Thumb Impression" },
    //     { key: "esi", value: "ESI" },
    //     { key: "fines", value: "Fines" },
    //     { key: "gross_salary", value: "Gross Salary" },
    //     { key: "hra", value: "HRA" },
    //     { key: "id", value: "ID" },
    //     { key: "insurance", value: "Insurance" },
    //     { key: "lwf", value: "LWF" },
    //     { key: "maternityBenefit", value: "Maternity Benefit" },
    //     { key: "medical_allowance", value: "Medical Allowance" },
    //     { key: "mode_of_pay", value: "Mode Of Payment" },
    //     { key: "net_pay", value: "Net Pay" },
    //     { key: "nfh", value: "NFH" },
    //     { key: "ot", value: "OT" },
    //     { key: "others", value: "Others" },
    //     { key: "p_month", value: "Payment Month" },
    //     { key: "pf", value: "PF" },
    //     { key: "pt", value: "PT" },
    //     { key: "salary_advance", value: "Salary Advance" },
    //     { key: "service_weightage", value: "Service Weightage" },
    //     { key: "society", value: "Society" },
    //     { key: "special_allowance", value: "Special Allowance" },
    //     { key: "st_bonus", value: "ST Bonus" },
    //     { key: "subsistanceAllowance", value: "Subsistance Allowance" },
    //     { key: "tds", value: "TDS" },
    //     { key: "total_deduction", value: "Total Deduction" }
    //     )

    // }else if(formattedTitle == "Maternity Benefits"){
    //     header.push(
    //         { key: "emp_code", value: "Employee Code", input: "text", readOnly:true   },
    //     { key: "id", value: "Record ID", input: "number" },
    //     { key: "unit_id", value: "Unit ID", input: "text" ,readOnly:true },
    //     { key: "client_id", value: "Client ID", input: "text" ,readOnly:true },
    //     { key: "woman_name", value: "Woman Name", input: "text" },
    //     { key: "work_nature", value: "Work Nature", input: "text" },
    //     { key: "father_or_husband_name", value: "Father/Husband Name", input: "text" },
    //     { key: "appointment_date", value: "Appointment Date", input: "date" },
    //     { key: "advance_maternity_benefit_amount", value: "Advance Maternity Benefit Amount", input: "number" },
    //     { key: "advance_maternity_benefit_date", value: "Advance Maternity Benefit Date", input: "date" },
    //     { key: "bonus_payment_amount_sec8", value: "Bonus Payment Amount (Sec 8)", input: "number" },
    //     { key: "bonus_payment_date_sec8", value: "Bonus Payment Date (Sec 8)", input: "date" },
    //     { key: "child_birth_date", value: "Child Birth Date", input: "date" },
    //     { key: "child_survives_benefit_period", value: "Child Survives Benefit Period", input: "text" },
    //     { key: "child_survives_benefit_recipient", value: "Child Survives Benefit Recipient", input: "text" },
    //     { key: "death_benefit_amount", value: "Death Benefit Amount", input: "number" },
    //     { key: "death_benefit_recipient", value: "Death Benefit Recipient", input: "text" },
    //     { key: "delivery_miscarriage_death_proof_date", value: "Delivery/Miscarriage Death Proof Date", input: "date" },
    //     { key: "discharge_dismissal_date", value: "Discharge/Dismissal Date", input: "date" },
    //     { key: "illness_leave_wages_amount_sec10", value: "Illness Leave Wages Amount (Sec 10)", input: "number" },
    //     { key: "illness_leave_wages_date_sec10", value: "Illness Leave Wages Date (Sec 10)", input: "date" },
    //     { key: "illness_proof_date_sec10", value: "Illness Proof Date (Sec 10)", input: "date" },
    //     { key: "leave_wages_payment_amount_sec9", value: "Leave Wages Payment Amount (Sec 9)", input: "number" },
    //     { key: "leave_wages_payment_date_sec9", value: "Leave Wages Payment Date (Sec 9)", input: "date" },
    //     { key: "notice_date_sec6", value: "Notice Date (Sec 6)", input: "date" },
    //     { key: "pregnancy_proof_date_sec6", value: "Pregnancy Proof Date (Sec 6)", input: "date" },
    //     { key: "subsequent_maternity_benefit_amount", value: "Subsequent Maternity Benefit Amount", input: "number" },
    //     { key: "subsequent_maternity_benefit_date", value: "Subsequent Maternity Benefit Date", input: "date" },
    //     { key: "woman_death_date", value: "Woman Death Date", input: "date" },
    //     { key: "employer_signature", value: "Employer Signature", input: "text" },
    //     { key: "employment_status_header_names", value: "Employment Status Header Names", input: "text" },
    //     { key: "employment_status_header_values", value: "Employment Status Header Values", input: "text" },
    //     { key: "establishment_name", value: "Establishment Name", input: "text" },
    //     { key: "inspector_remarks", value: "Inspector Remarks", input: "text" },
    //     )

    // }else if(formattedTitle == "Salary Advances"){
    //     header.push(
    //         { key: "emp_code", value: "Employee Code", input: "text", readOnly:true   },
    //     { key: "id", value: "Record ID", input: "number" },
    //     { key: "unit_id", value: "Unit ID", input: "text" ,readOnly:true },
    //     { key: "client_id", value: "Client ID", input: "text",readOnly:true  },
    //     { key: "date_of_advance_made", value: "Date of Advance Made", input: "date" },
    //     { key: "amount_of_advance_made", value: "Amount of Advance Made", input: "number" },
    //     { key: "purpose_advance_made", value: "Purpose of Advance Made", input: "text" },
    //     { key: "number_of_instalments_advance_repaid", value: "Number of Instalments Repaid", input: "number" },
    //     { key: "postponements", value: "Postponements", input: "text" },
    //     { key: "granted", value: "Granted", input: "text" },
    //     { key: "date_total_on_amount_repaid", value: "Date Total on Amount Repaid", input: "date" },
    //     { key: "remarks", value: "Remarks", input: "text" },
    //     )

    // }else if(formattedTitle == "Holidays"){
    //     header.push(
    //         { key: "hol_state", value: "State Holidays", input: "text" },
    //         { key: "hol_name", value: "Holiday Names", input: "text" },
    //         { key: "hol_date", value: "Holidays Date", input: "date" },
    //         { key: "hol_type", value: "Holiday Type", input: "text" },
    //         { key: "current_year", value: "Current Year", input: "text" }
    //     )

    // }else if(formattedTitle == "States"){
    //     header.push(
    //     { key: "state_name", value: "State Name", input: "text" },
    //     { key: "state_code", value: "State Code", input: "text" },
    //     )

    // }else if(formattedTitle == "Clientunitwise Holidays"){
    //     header.push(
    //         { key: "new_id", value: "Holiday Name", input: "holiday" },
    //     { key: "getHolidayDate", value: "Holiday Date", input: "date" ,readOnly:true },
    //     { key: "client_id", value: "Client ID", input: "text" , readOnly:true },
    //     { key: "unit_id", value: "Unit ID", input: "text" , readOnly:true },
    //     { key: "year", value: "Current Year", input: "text",readOnly:true },
    //     )

    // }

    const updateCheckAllState = () => {
        if (data) {
            const allChecked = data.length > 0 && data.every(item => checkedItems[item.id]);
            setIsAllChecked(allChecked);
        }
    };
    useEffect(() => {
        updateCheckAllState();
    }, [checkedItems]);
    useEffect(() => {
        const count = Object.values(checkedItems).filter(Boolean).length;
        setCheckedItemCount(count);
    }, [checkedItems]);
    const handleCheckChange = (id) => {
        setCheckedItems(prevCheckedItems => ({
            ...prevCheckedItems,
            [id]: !prevCheckedItems[id]
        }));
    };
    const handleCheckAll = () => {
        const newCheckedItems = { ...checkedItems };
        const visibleRows = $('#dataTable tbody tr:visible');

        const allVisibleChecked = visibleRows.toArray().every(row => {
            const id = $(row).data('id');
            return checkedItems[id];
        });

        const shouldCheck = !allVisibleChecked;

        visibleRows.each(function () {
            const id = $(this).data('id');
            newCheckedItems[id] = shouldCheck;
        });

        setCheckedItems(newCheckedItems);
    };
    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return "";
        return sortConfig.direction === "asc" ? "▲" : "▼";
    };
    const calculateWidth = (text) => {
        if (text) {
            const charWidth = 11;
            return `${text.length * charWidth}px`;
        }
    };

    function formatDate(dateString) {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            // Handle the case where dateString is invalid
            return "Invalid Date";
        }

        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Intl.DateTimeFormat('en-GB', options).format(date).replace(/ /g, '-');
    }

    const submitdownload = async (data) => {
        setLoading(true);
        try {
            const response = await dispatch(downloadAllSelectedIDsRecords(data));
            if (response.success == true) {
                showSnackbar("downloadded", 'success');
                setLoading(false);
            } else {
                showSnackbar(response.error, 'warning');
                setLoading(false);
            }
        } catch (error) {
            showSnackbar('An error occurred', 'error');
            setLoading(false);
        }
    }
    const handledeletemultialert = (e) => {
        e.preventDefault()
        const itemDatas = data;
        const buttonClicked = e.nativeEvent.submitter.value;
        if (checkedItemCount == 0) {
            showSnackbar("select the row", 'warning');
        } else {

                if(formattedTitle == 'ClientUnitWise Holidays'){
                    const selectedIds = Object.keys(checkedItems).filter(id => checkedItems[Number(id)]).map(id => Number(id));
                    let storedId = [];
                    let storedHoliId = [];
                    selectedIds.forEach(datas => {
                        const item = itemDatas.find(d => d.id === datas);
                        storedId.push(item.row_id);
                        storedHoliId.push(item.old_id);
                    });

                    const client_id = sessionStorage.getItem('selectedClient')
                    const data = {
                        client_id: client_id,
                        current_month: date,
                        titleName: formattedTitle,
                        selectedIds: storedHoliId,
                        ids:storedId
                    }
                    submitdownload(data);

                }else{
                    const selectedIds = Object.keys(checkedItems).filter(id => checkedItems[Number(id)]).map(id => Number(id));
                    const client_id = sessionStorage.getItem('selectedClient')
                    const data = {
                        client_id: client_id,
                        current_month: date,
                        titleName: formattedTitle,
                        selectedIds: selectedIds
                    }
                    submitdownload(data);
                }

        }
    }
    const generateMonthOptions = () => {
        return availableYearMonths.flatMap(({ year, month }) =>
          month.map((m) => {
            const mm = m.toString().padStart(2, "0");
            return {
              value: `${year}-${mm}`,
              label: `${year}-${mm}`,
            };
          })
        );
      };

      const generateYearOptions = () => {
        return availableYearMonths.map(({ year }) => ({
          value: year,
          label: year,
        }));
      };
      const handleSubmit = () => {
        if (!fromYear || !toMonth) {
          alert("Please select both Year and Month");
          return;
        }

        const selectedValue = `${fromYear}-${toMonth}-01`;
        window.location.href = `/reports/report/${reportTitle}/${selectedValue}`;
      };
      useEffect(() => {
        if (getDate?.length) {
          setFromYear(getDate[0]);
          setToMonth(getDate[1]?.toString().padStart(2, '0'));
        }
      }, []);
      const handleSubmitYear = () => {
        if (!fromDate || !toDate) {
          alert("Please select both From and To dates");
          return;
        }

        // Do something with the selected dates
        console.log("From Date:", fromDate); // e.g., 2024-05-01
        console.log("To Date:", toDate);     // e.g., 2024-05-20

        // Example redirect
        const reportTitle = "employee-master";
        window.location.href = `/reports/report/${reportTitle}/${fromDate}/${toDate}`;
      };

  return (
    <div className="col-md-12 py-3">
                <div className="card-body">
                    {availableYearMonths && !usesDedicatedReportApi &&
                        <div className="row">
                            <div className="col-md-12 mb-3">
                                <label>Filter Type:</label>
                                <div>
                                <button
                                    className={`btn btn-sm me-2 ${filterType === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setFilterType("monthly")}
                                >
                                    Monthly
                                </button>
                                <button
                                    className={`btn btn-sm ${filterType === "yearly" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setFilterType("yearly")}
                                >
                                   Custom Date
                                </button>
                                </div>
                            </div>

                            {filterType === "monthly" && (
                                <>
                                        <div className="col-md-2 mb-3">
                                        <label>Year:</label>
                                        <select
                                            className="form-select"
                                            value={fromYear}
                                            onChange={(e) => setFromYear(e.target.value)}
                                        >
                                            <option value="">Select Year</option>
                                            {availableYearMonths.map((opt, index) => (
                                            <option key={index} value={opt.year}>
                                                {opt.year}
                                            </option>
                                            ))}
                                        </select>
                                        </div>

                                        <div className="col-md-2 mb-3">
                                        <label>Month:</label>
                                        <select
                                            className="form-select"
                                            id=''
                                            value={toMonth}
                                            onChange={(e) => setToMonth(e.target.value)}
                                        >
                                            <option value="">Select Month</option>
                                            {availableYearMonths
                                            .find((y) => y.year === parseInt(fromYear))
                                            ?.month.map((m) => (
                                                <option key={m} value={m.toString().padStart(2, '0')}>
                                                {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                                                </option>
                                            ))}
                                        </select>
                                        </div>

                                        <div className="col-md-1 text-end">
                                            <button className="btn btn-success mt-4" onClick={handleSubmit}>
                                                Submit
                                            </button>
                                        </div>

                                </>
                            )}


                            {filterType === "yearly" && (
                                <>
                                {/* <div className="col-md-2 mb-3">
                                    <label>From:</label><br/>
                                    <input type="date" name="" id="" className='form-control'/>
                                    <select className="form-select" value={fromYear} onChange={(e) => setFromYear(e.target.value)}>
                                    <option value="">Select</option>
                                    {generateMonthOptions().map((opt, index) => (
                                        <option key={index} value={opt.value}>
                                        {opt.label}
                                        </option>
                                    ))}
                                    </select>
                                </div>
                                <div className="col-md-2 mb-3">
                                    <label>To :</label><br/>
                                    <input type="date" name="" id="" className='form-control' />
                                    <select className="form-select" value={toYear} onChange={(e) => setToYear(e.target.value)}>
                                    <option value="">Select</option>
                                    {generateMonthOptions().map((opt, index) => (
                                        <option key={index} value={opt.value}>
                                        {opt.label}
                                        </option>
                                    ))}
                                    </select>
                                </div>
                                <div className="col-md-1 text-end">
                                    <button className="btn btn-success mt-3" onClick={handleSubmit}>
                                        Submit
                                    </button>
                                </div> */}
                                    <div className="col-md-2 mb-3">
                                        <label>From:</label><br />
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                        </div>

                                        <div className="col-md-2 mb-3">
                                        <label>To:</label><br />
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                        </div>

                                        <div className="col-md-1 text-end">
                                        <button className="btn btn-success mt-3" onClick={handleSubmitYear}>
                                            Submit
                                        </button>
                                    </div>

                                </>
                            )}
                        </div>
                    }

                    {usesRegistryReport ? (
                        <>
                            <div className="reports-detail-back">
                                <button
                                    type="button"
                                    className="reports-back-btn"
                                    onClick={() => navigate(`/report`)}
                                >
                                    ← Back to Reports
                                </button>
                            </div>
                            <ReportEditableTable
                                config={registryConfig}
                                data={data}
                                headerColumns={header}
                                date={date}
                                setLoading={setLoading}
                                showSnackbar={showSnackbar}
                                monthfilterreport={periodFilters}
                                title={formattedTitle}
                            />
                        </>
                    ) : (
                    <div className="row">
                        <div className="col-md-12">

                            <div className="card mt-0">
                                <form onSubmit={handledeletemultialert}>
                                    <div className="card-header">
                                        <div className="col-md-12">
                                            <div className="row justify-content-between">
                                                <div className="col-md-6">
                                                    <div className="row">
                                                        <p className='pt-1 m-0 client-2nd-header-size col-md-3'>Selected Entries: {checkedItemCount}</p>

                                                        {/* <div className='col-md-4'>
                                                            {title != "Holiday" ?(<></>):(
                                                                <select class="form-select py-0 selunitoption mt-1" aria-label="Default select example" onChange={handleClientChange}>
                                                                    <option value="" className='selunitoption text-dark'>
                                                                        All unit
                                                                    </option>
                                                                    {uniqueClients.map((client, index) => (
                                                                        <option key={index} value={client.unit_id} className='selunitoption text-dark'>
                                                                            {client.unit_id}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                )}

                                                        </div> */}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="row float-end">
                                                        <p className='col-md-11 pt-1 client-2nd-header-size m-0'>Total Entries: {data ? data.length : 0}</p>
                                                        <div className="dropdown col-md-1 p-0 bg-transparent">
                                                            <button className=" p-0 pt-2 w-0 border-0 btn-transparent " type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                                <i className="bi bi-three-dots-vertical"></i>
                                                            </button>
                                                            <ul className="dropdown-menu btn-z-indexer">
                                                                <li><button className="dropdown-item " type='submit' value="downloadData">Download Selected</button></li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body m-0 p-0">
                                        <div className="table-responsive tablefontsize">
                                            {header.length !== 0 ? (
                                                <table className='table table-bordered' id='dataTable'>
                                                    <thead className='sticky-top sticky-col-form'>
                                                        <tr>
                                                            <th className='card-header-color  text-dark'><input type="checkbox" checked={isAllChecked} onChange={handleCheckAll} name="" id="" /></th>
                                                            <th className='slnowidth  card-header-color  text-dark'><span>SL NO</span></th>
                                                             {/* {header.map(headerItem => (
                                                                <th key={headerItem.key} style={{ minWidth: calculateWidth(headerItem.value) }} className='  card-header-color  text-dark'>{headerItem.value} {getSortIcon(headerItem.key)}</th>
                                                            ))} */}

                                                            {header.filter(row => row.key != "id").map((headerItem) => (
                                                                    <th
                                                                        key={headerItem.key}
                                                                        onClick={() => handleSort(headerItem.key)}
                                                                        style={{ minWidth: calculateWidth(headerItem.label), cursor: "pointer" }}
                                                                        className="card-header-color text-dark"
                                                                    >
                                                                        {headerItem.label} {getSortIcon(headerItem.key)}
                                                                    </th>
                                                                ))}

                                                        </tr>
                                                    </thead>
                                                    <tbody className='sticky-col-form'>
                                                        {data && data.length > 0 ? (
                                                            <>
                                                                {data.map((item, index) => (
                                                                    <tr key={item.id ?? index} data-id={item.id}>
                                                                        <td className='tabletdheight'>
                                                                            <input type="checkbox" checked={checkedItems[item.id] || false} onChange={() => handleCheckChange(item.id)} />
                                                                        </td>
                                                                        <td className='tabletdheight'>{index + 1}</td>
                                                                            {header.filter((headerItem) => headerItem.key !== 'id').map((headerItem) => (
                                                                                <td key={headerItem.key}>
                                                                                    <span className='tabletdheight'>{item[headerItem.key]}</span>
                                                                                </td>
                                                                            ))}

                                                                    </tr>
                                                                ))}
                                                            </>
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={header.length} className="text-center fs-3 fw-bold text-muted">
                                                                    No Record Available
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>

                                                </table>


                                            ) : (
                                                <h1></h1>
                                            )}
                                        </div>


                                    </div>
                                </form>
                            </div>

                        </div>
                    </div>
                    )}
                </div>
        </div>
  )
}

export default ReportViews
